"use client"

import { ENTERPRISE_INQUIRY_RECIPIENT } from "@/lib/email/sales-inbox"
import type { NotificationChannelType } from "@/lib/notifications/types"
import { NotificationGroup } from "./notification-group"
import { NotificationRow } from "./notification-row"

interface OtherMessage {
  label: string
  description: string
  channelType: NotificationChannelType
  from: string
}

interface OtherMessageGroup {
  id: string
  title: string
  audience: string
  messages: readonly OtherMessage[]
}

const TENANT_MAILBOX = "The tenant's connected Gmail, else the platform mailbox."

/** Sent outside the notification catalog; a new message of this kind belongs here until it joins the catalog. */
const OTHER_MESSAGE_GROUPS: readonly OtherMessageGroup[] = [
  {
    id: "account",
    title: "Account emails",
    audience: "Sent to the person the action concerns.",
    messages: [
      {
        label: "Email verification",
        description:
          "Confirms a new account's email: after sign-up, when a Shopify store owner claims an account, and when the user asks for it again.",
        channelType: "email",
        from: "The platform mailbox; a repeated one comes from the tenant's connected Gmail when it has one.",
      },
      {
        label: "Password reset",
        description: "Someone asked to reset an account's password.",
        channelType: "email",
        from: TENANT_MAILBOX,
      },
      {
        label: "Invitation",
        description: "An admin invited a user to a tenant; the email links to setting a password.",
        channelType: "email",
        from: TENANT_MAILBOX,
      },
      {
        label: "Invitation reminder",
        description: "An admin resent a pending invitation.",
        channelType: "email",
        from: TENANT_MAILBOX,
      },
    ],
  },
  {
    id: "operators",
    title: "Operator emails",
    audience: "Sent to every active super user and integrator, outside Platform events.",
    messages: [
      {
        label: "Plan expiry summary",
        description: "The daily plan-expiry run deactivated one or more tenants.",
        channelType: "email",
        from: "The platform mailbox.",
      },
    ],
  },
  {
    id: "sales",
    title: "Sales emails",
    audience: `Sent to the sales inbox, ${ENTERPRISE_INQUIRY_RECIPIENT}.`,
    messages: [
      {
        label: "Enterprise inquiry",
        description: "Someone asked about the Enterprise plan through Contact Sales at signup.",
        channelType: "email",
        from: "The platform mailbox.",
      },
    ],
  },
  {
    id: "telegram",
    title: "Telegram bot replies",
    audience: "Sent in the chat that wrote to a notification bot.",
    messages: [
      {
        label: "Chat not subscribed",
        description: "Someone sent /start to a notification bot without the enrollment code; the bot explains how to subscribe.",
        channelType: "telegram",
        from: "The channel's notification bot.",
      },
    ],
  },
]

export function OtherMessagesTab() {
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Messages the platform sends outside the notification catalog. They are always sent, cannot be edited here yet,
        and are not recorded in the delivery log.
      </p>
      {OTHER_MESSAGE_GROUPS.map((group) => (
        <NotificationGroup key={group.id} id={`other-group-${group.id}`} title={group.title} description={group.audience}>
          {group.messages.map((message) => (
            <NotificationRow
              key={message.label}
              label={message.label}
              description={message.description}
              channelTypes={[message.channelType]}
            >
              <p className="text-xs text-muted-foreground">{`From: ${message.from}`}</p>
            </NotificationRow>
          ))}
        </NotificationGroup>
      ))}
    </div>
  )
}
